// SCR-STAFF-931 (AP-ORG-02؛ US-ORG-011): واحدهای قابل رزرو (تخت یا صندلی) یک اتاق؛ افزودن با کد یکتا در اتاق، غیرفعال‌سازی و فعال‌سازی. اتاق دارای
// واحد فعال فقط از راه واحدهایش رزرو می‌شود (BR-ORG-011). ظرفیت همزمان هر واحد (AP-ORG-03؛ US-ORG-013).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type BookableUnitView, type RoomView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { ORG_CODE_PATTERN } from './common';
import { deviceKeys } from './deviceCommon';

export function RoomUnitsDialog({ room, onClose }: { room: RoomView; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.room.manage');
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const titleId = useId();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  const units = useQuery({
    queryKey: [...deviceKeys.units(session?.tenantId), room.id],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/bookable-units', { params: { query: { roomId: room.id } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: deviceKeys.units(session?.tenantId) });

  const add = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/organization/bookable-units', {
          body: { roomId: room.id, code: code.trim(), name: name.trim(), sortOrder: (units.data?.items.length ?? 0) + 1 },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setCode('');
      setName('');
      refresh();
    },
  });
  // ظرفیت همزمان واحد (US-ORG-013؛ AC-ORG-013.1) ← org.bookable_unit.updated.v1 ← SCH.
  const resize = useMutation({
    mutationFn: ({ unit, capacity }: { unit: BookableUnitView; capacity: number }) =>
      unwrap(tenantApi.PUT('/api/v1/organization/bookable-units/{id}', { params: { path: { id: unit.id } }, body: { name: unit.name, sortOrder: Number(unit.sortOrder), capacity, rowVersion: Number(unit.rowVersion) } })),
    onSuccess: refresh,
  });
  const toggle = useMutation({
    mutationFn: (unit: BookableUnitView) =>
      unit.status === 'Active'
        ? unwrap(tenantApi.POST('/api/v1/organization/bookable-units/{id}:deactivate', { params: { path: { id: unit.id } } }))
        : unwrap(tenantApi.POST('/api/v1/organization/bookable-units/{id}:activate', { params: { path: { id: unit.id } } })),
    onSuccess: refresh,
  });

  const columns: DataTableColumn<BookableUnitView>[] = [
    { id: 'code', headerKey: 'org:units.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'name', headerKey: 'org:units.columns.name', cell: (row) => row.name },
    {
      id: 'capacity',
      headerKey: 'org:units.columns.capacity',
      cell: (row) => (
        <TextField
          select
          size="small"
          value={Number(row.capacity)}
          onChange={(event) => resize.mutate({ unit: row, capacity: Number(event.target.value) })}
          disabled={!write.canWrite || resize.isPending}
          slotProps={{ htmlInput: { 'aria-label': t('org:units.columns.capacity'), 'data-testid': `unit-capacity-${row.code}` } }}
        >
          {Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
            <MenuItem key={value} value={value}>
              {faDigits(value)}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      id: 'status',
      headerKey: 'org:units.columns.status',
      cell: (row) => <StatusChip tone={row.status === 'Active' ? 'active' : 'inactive'} />,
    },
    {
      id: 'actions',
      headerKey: 'org:units.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => toggle.mutate(row)} disabled={!write.canWrite || toggle.isPending} data-testid={`unit-toggle-${row.code}`}>
          {row.status === 'Active' ? t('org:units.deactivate') : t('org:units.activate')}
        </Button>
      ),
    },
  ];

  const codeInvalid = code.length > 0 && !ORG_CODE_PATTERN.test(code.trim());
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{t('org:units.title', { name: room.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="info">{t('org:units.hint')}</Alert>
          <DataTable
            captionKey="org:units.caption"
            columns={columns}
            rows={units.data?.items}
            getRowId={(row) => row.id}
            isLoading={units.isPending}
            error={units.error}
            onRetry={() => void units.refetch()}
            emptyTitleKey="org:units.empty"
          />
          {write.canWrite && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label={t('org:units.code')}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                error={codeInvalid}
                helperText={codeInvalid ? t('errors:validation.org_code') : undefined}
                slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'unit-code' } }}
              />
              <TextField
                size="small"
                label={t('org:units.name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'unit-name' } }}
              />
              <Button
                variant="contained"
                onClick={() => add.mutate()}
                disabled={add.isPending || codeInvalid || !code.trim() || !name.trim()}
                data-testid="unit-add"
              >
                {t('org:units.add')}
              </Button>
            </Stack>
          )}
          {add.error ? <ErrorState error={add.error} compact /> : null}
          {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
          {resize.error ? <ErrorState error={resize.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
